import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { I18NextPipe } from 'angular-i18next';
import { ToastService } from 'mbs-ui-kit';
import { combineLatest, of } from 'rxjs';
import { map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { SnmpService } from '../../services/snmp.service';
import * as SnmpAlertActions from './actions';

@Injectable()
export class SnmpCustomNotificationsEffects {
  loadSnmpAlerting$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SnmpAlertActions.loadAlertingInfo),
      switchMap(() =>
        combineLatest([this.smnpService.getDeviceAlerts(), this.smnpService.getDeviceCustomAlerts(), this.smnpService.getDeviceTypes()])
      ),
      map(([alertTypes, customisedAlerts, deviceTypes]) =>
        SnmpAlertActions.fullLoadComplete({
          alertTypes: alertTypes.concat(customisedAlerts),
          deviceTypes
        })
      )
    );
  });

  loadSnmpDeviceTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SnmpAlertActions.loadSnmpDeviceTypes),
      switchMap(() => this.smnpService.getDeviceTypes()),
      map((deviceTypes) => SnmpAlertActions.setSnmpDeviceTypes({ deviceTypes }))
    );
  });

  loadSnmpAlertTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SnmpAlertActions.loadSnmpAlertTypes),
      switchMap(() => this.smnpService.getDeviceAlerts()),
      map((alertTypes) => SnmpAlertActions.setSnmpAlertTypes({ alertTypes }))
    );
  });

  loadSnmpAlertCustomisation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SnmpAlertActions.loadSnmpAlertCustomisation),
      switchMap(() => this.smnpService.getDeviceCustomAlerts()),
      map((customisedAlerts) => SnmpAlertActions.setSnmpAlertTypes({ alertTypes: customisedAlerts }))
    );
  });

  addCustomProperties = createEffect(() => {
    return this.actions$.pipe(
      ofType(SnmpAlertActions.addCustomProperties),
      mergeMap(({ deviceType, property }) =>
        this.smnpService.saveDeviceCustomProperties(deviceType, property).pipe(
          tap((value) =>
            this.toastService.success(
              this.i18n.transform('mbs-ui:snmp.pinnedSuccessMessage'),
              this.i18n.transform('mbs-ui:snmp.pinnedSuccessTitle')
            )
          ),
          switchMap((response: any) => of(SnmpAlertActions.loadDevicePropertiesCustom({ deviceType })))
        )
      )
    );
  });

  updateCustomProperties = createEffect(() => {
    return this.actions$.pipe(
      ofType(SnmpAlertActions.updateCustomProperties),
      mergeMap(({ deviceType, snmpDeviceTypeGuid, property }) =>
        this.smnpService.updateDeviceCustomProperties(deviceType, snmpDeviceTypeGuid, property).pipe(
          tap((value) =>
            this.toastService.success(
              this.i18n.transform('mbs-ui:snmp.updateSuccessMessage'),
              this.i18n.transform('mbs-ui:snmp.updateSuccessTitle')
            )
          ),
          switchMap((response: any) => of(SnmpAlertActions.loadDevicePropertiesCustom({ deviceType })))
        )
      )
    );
  });

  removeCustomProperties = createEffect(() => {
    return this.actions$.pipe(
      ofType(SnmpAlertActions.removeCustomProperties),
      mergeMap(({ deviceType, snmpDeviceTypeGuid }) =>
        this.smnpService.deleteDeviceCustomProperties(deviceType, snmpDeviceTypeGuid).pipe(
          tap((value) =>
            this.toastService.success(
              this.i18n.transform('mbs-ui:snmp.unPinnedSuccessMessage'),
              this.i18n.transform('mbs-ui:snmp.unPinnedSuccessTitle')
            )
          ),
          switchMap((response: any) => of(SnmpAlertActions.loadDevicePropertiesCustom({ deviceType })))
        )
      )
    );
  });

  customProperties$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SnmpAlertActions.loadDevicePropertiesCustom),
      mergeMap(({ deviceType }) =>
        this.smnpService
          .getDeviceCustomProperties(deviceType)
          .pipe(switchMap((properties: any) => of(SnmpAlertActions.setCustomProperties({ snmpDeviceCustomProperty: properties }))))
      )
    );
  });

  constructor(
    private actions$: Actions,
    private smnpService: SnmpService,
    private store: Store,
    private i18n: I18NextPipe,
    private toastService: ToastService
  ) {}
}
